/**
 * Schema Definitions that power Protocol Acceleration in the Resolution API.
 */

import type {
  Address,
  ChainId,
  DomainId,
  InterpretedName,
  Node,
  RecordVersion,
  ResolverId,
  ResolverRecordsId,
} from "enssdk";
import { index, onchainTable, primaryKey, relations, uniqueIndex } from "ponder";

/**
 * Tracks an Account's ENSIP-19 Reverse Name Records by CoinType.
 *
 * NOTE: this is NOT a cohesive, materialized index of ALL of an account's Primary Names, it is ONLY
 * an index of its ENSIP-19 Reverse Name _Records_ stored by a StandaloneReverseRegistrar:
 * - default.reverse
 * - [coinType].reverse
 * - NOT *.addr.reverse
 *
 * So these records CANNOT be queried directly and used as a source of truth — you MUST perform
 * Forward Resolution to resolve a consistent set of an Account's ENSIP-19 Primary Names. These records
 * are used to power Protocol Acceleration for those ReverseResolvers backed by a StandloneReverseRegistrar.
 */
export const reverseNameRecord = onchainTable(
  "reverse_name_records",
  (t) => ({
    // keyed by (address, coinType)
    address: t.hex().notNull().$type<Address>(),
    // @TODO(cointype-bigint): store as `t.int8({ mode: "number" }).$type<CoinType>()` (like chainId
    // elsewhere) so reads/writes use CoinType directly without bigint round-trips. See #2293.
    coinType: t.bigint().notNull(),

    /**
     * Represents the ENSIP-19 Reverse Name Record for a given (address, coinType).
     *
     * The value of this field is guaranteed to be a non-empty {@link InterpretedName}.
     */
    value: t.text().notNull().$type<InterpretedName>(),
  }),
  (t) => ({
    pk: primaryKey({ columns: [t.address, t.coinType] }),
  }),
);

/**
 * Tracks Domain-Resolver Relationships. This powers:
 *  1. Domain-Resolver Realtionships within the GraphQL API, and
 *  2. Accelerated lookups of a Domain's Resolver within the Resolution API.
 *
 * It is keyed by (chainId, address, domainId) to match the on-chain datamodel of
 * Registry/(shadow)Registry Domain-Resolver relationships.
 */
export const domainResolverRelation = onchainTable(
  "domain_resolver_relations",
  (t) => ({
    // keyed by (chainId, address, node)
    chainId: t.int8({ mode: "number" }).notNull().$type<ChainId>(),

    // The Registry (ENSv1Registry or ENSv2Registry)'s AccountId.
    address: t.hex().notNull().$type<Address>(),
    domainId: t.text().notNull().$type<DomainId>(),

    // The Domain's assigned Resolver's address (NOTE: always scoped to chainId)
    resolver: t.hex().notNull().$type<Address>(),
  }),
  (t) => ({
    pk: primaryKey({ columns: [t.chainId, t.address, t.domainId] }),
    byDomain: index().on(t.domainId),
  }),
);

export const domainResolverRelation_relations = relations(domainResolverRelation, ({ one }) => ({
  resolver: one(resolver, {
    fields: [domainResolverRelation.chainId, domainResolverRelation.resolver],
    references: [resolver.chainId, resolver.address],
  }),
}));

/**
 * Resolver represents an individual IResolver contract that has emitted at least 1 event.
 * Note that Resolver contracts can exist on-chain but not emit any events and still function
 * properly, so checks against a Resolver's existence and metadata must be done at runtime.
 */
export const resolver = onchainTable(
  "resolvers",
  (t) => ({
    // keyed by (chainId, address)
    id: t.text().primaryKey().$type<ResolverId>(),

    chainId: t.int8({ mode: "number" }).notNull().$type<ChainId>(),
    address: t.hex().notNull().$type<Address>(),

    /**
     * Whether this Resolver implements ENSIP-10 wildcard resolution (`IExtendedResolver`,
     * interfaceId `0x9061b923`), determined via a single `supportsInterface` RPC the first
     * time the Resolver is observed (see `upsertResolver`).
     */
    isExtended: t.boolean().notNull().default(false),
  }),
  (t) => ({
    byId: uniqueIndex().on(t.chainId, t.address),
  }),
);

export const resolver_relations = relations(resolver, ({ many }) => ({
  records: many(resolverRecords),
}));

/**
 * Tracks a set of records for a specified `node` within a `resolver` contract on `chainId`.
 *
 * ResolverRecords is keyed by (chainId, resolver, node) and:
 * - has one `name` record (see ENSIP-3)
 * - has many `addressRecords` (unique by coinType) (see ENSIP-9)
 * - has many `textRecords` (unique by key) (see ENSIP-5)
 *
 * It is keyed by (chainId, resolver, node) to match the on-chain datamodel of Resolver contract storage.
 *
 * WARNING: These record values do NOT allow the caller to confidently resolve records for names
 * without following Forward Resolution according to the ENS protocol: a direct query to the database
 * for a record's value is not ENSIP-10 nor CCIP-Read compliant.
 */
export const resolverRecords = onchainTable(
  "resolver_records",
  (t) => ({
    // keyed by (chainId, resolver, node)
    id: t.text().primaryKey().$type<ResolverRecordsId>(),

    chainId: t.int8({ mode: "number" }).notNull().$type<ChainId>(),
    address: t.hex().notNull().$type<Address>(),
    node: t.hex().notNull().$type<Node>(),

    /**
     * Represents the value of the reverse-resolution (ENSIP-3) name() record, used for Reverse Resolution.
     *
     * If present, the value of this field is guaranteed to be a non-empty {@link InterpretedName}.
     */
    name: t.text().$type<InterpretedName>(),

    /**
     * ENSIP-7 contenthash raw bytes or null if not set.
     */
    contenthash: t.hex(),

    /**
     * PubkeyResolver (x, y) pair, or null if not set.
     *
     * Invariant: both null together, or both set together.
     */
    pubkeyX: t.hex(),
    pubkeyY: t.hex(),

    /**
     * IDNSZoneResolver zonehash or null if not set.
     */
    dnszonehash: t.hex(),

    /**
     * IVersionableResolver version. Null when no `VersionChanged` event has been seen for this
     * (chainId, address, node) — the resolver may not implement `IVersionableResolver`, or simply
     * may never have been version-bumped. Consumers should treat null as "unknown" rather than 0.
     */
    version: t.bigint().$type<RecordVersion>(),
  }),
  (t) => ({
    byId: uniqueIndex().on(t.chainId, t.address, t.node),
  }),
);

export const resolverRecords_relations = relations(resolverRecords, ({ one, many }) => ({
  // belongs to resolver
  resolver: one(resolver, {
    fields: [resolverRecords.chainId, resolverRecords.address],
    references: [resolver.chainId, resolver.address],
  }),

  // resolverRecord has many address records
  addressRecords: many(resolverAddressRecord),

  // resolverRecord has many text records
  textRecords: many(resolverTextRecord),
}));

/**
 * Tracks address records for a `node` by `coinType` within a `resolver` on `chainId`.
 *
 * ResolverAddressRecord is keyed by (chainId, resolver, node, coinType), where the composite key
 * segment (chainId, resolver, node) describes a ResolverRecord entity. A ResolverAddressRecord is
 * then additionally keyed by (coinType).
 */
export const resolverAddressRecord = onchainTable(
  "resolver_address_records",
  (t) => ({
    // keyed by ((chainId, resolver, node), coinType)
    chainId: t.int8({ mode: "number" }).notNull().$type<ChainId>(),
    address: t.hex().notNull().$type<Address>(),
    node: t.hex().notNull().$type<Node>(),
    // NOTE: all well-known CoinTypes fit into javascript number but NOT postgres .integer, must be
    // stored as BigInt
    // @TODO(cointype-bigint): use `t.int8({ mode: "number" }).$type<CoinType>()` like `chainId` above
    // to drop bigint round-trips at every read/write boundary. See #2293.
    coinType: t.bigint().notNull(),

    /**
     * Represents the value of the Address Record specified by ((chainId, resolver, node), coinType).
     *
     * The value of this field is interpreted by `interpretAddressRecordValue` — see its implementation
     * for additional context and specific guarantees.
     */
    value: t.hex().notNull(),
  }),
  (t) => ({
    pk: primaryKey({ columns: [t.chainId, t.address, t.node, t.coinType] }),
    // supports the reverse lookup powering `Account.nameReferences`:
    // `WHERE value = <address> [AND coin_type = <coinType>]`
    byValueAndCoinType: index().on(t.value, t.coinType),
  }),
);

export const resolverAddressRecordRelations = relations(resolverAddressRecord, ({ one }) => ({
  // belongs to resolverRecord
  resolver: one(resolverRecords, {
    fields: [
      resolverAddressRecord.chainId,
      resolverAddressRecord.address,
      resolverAddressRecord.node,
    ],
    references: [resolverRecords.chainId, resolverRecords.address, resolverRecords.node],
  }),
}));

/**
 * Tracks text records for a `node` by `key` within a `resolver` on `chainId`.
 *
 * ResolverTextRecord is keyed by (chainId, resolver, node, key), where the composite key
 * segment (chainId, resolver, node) describes a ResolverRecord entity. A ResolverTextRecord is
 * then additionally keyed by (key).
 */
export const resolverTextRecord = onchainTable(
  "resolver_text_records",
  (t) => ({
    // keyed by ((chainId, resolver, node), key)
    chainId: t.int8({ mode: "number" }).notNull().$type<ChainId>(),
    address: t.hex().notNull().$type<Address>(),
    node: t.hex().notNull().$type<Node>(),
    key: t.text().notNull(),

    /**
     * Represents the value of the Text Record specified by ((chainId, resolver, node), key).
     *
     * The value of this field is interpreted by `interpretTextRecordValue` — see its implementation
     * for additional context and specific guarantees.
     */
    value: t.text().notNull(),
  }),
  (t) => ({
    pk: primaryKey({ columns: [t.chainId, t.address, t.node, t.key] }),
  }),
);

export const resolverTextRecordRelations = relations(resolverTextRecord, ({ one }) => ({
  // belongs to resolverRecord
  resolver: one(resolverRecords, {
    fields: [resolverTextRecord.chainId, resolverTextRecord.address, resolverTextRecord.node],
    references: [resolverRecords.chainId, resolverRecords.address, resolverRecords.node],
  }),
}));
